/* Brightivity library (brightivity.dev/library — M4). Responses are {success, data}; api() returns {ok, data}. */
import { API_BASE } from '../config.js';
import { api, getSession } from './client.js';

const unwrap = (r) => (r.ok ? { ok: true, ...(r.data && r.data.data ? r.data.data : r.data) } : { ok: false, error: r.error, status: r.status });

/** → {ok, items:[{id, name, thumbnailUrl, createdAt, updatedAt, ...}]} */
export async function recentDesigns(limit = 4) {
  const r = unwrap(await api(`/library/designs?limit=${limit}&sort=newest`));
  return r.ok ? { ok: true, items: r.designs || [] } : { ok: false, items: [], error: r.error, status: r.status };
}

export async function listDesigns({ page = 1, limit = 20, search = '', sort = 'newest', category = '', source = '' } = {}) {
  const q = new URLSearchParams({ page: String(page), limit: String(limit), sort });
  if (search) q.set('search', search);
  if (category) q.set('category', category);
  if (source) q.set('source', source);
  return unwrap(await api(`/library/designs?${q}`));
}
export const librarySummary = async () => unwrap(await api('/library/summary'));
export const getCategories = async () => unwrap(await api('/library/categories'));
export const putCategories = async (categories) => unwrap(await api('/library/categories', { method: 'PUT', body: { categories } }));
export const updateDesign = async (id, patch) => unwrap(await api(`/library/designs/${id}`, { method: 'PUT', body: patch }));
export const deleteDesign = async (id) => unwrap(await api(`/library/designs/${id}`, { method: 'DELETE' }));

/** Multipart save (upload, printables, AI posters). fields: {name, source, category?, imageUrl?, metadata?, width?, height?}, files: {thumbnail?, file?} */
export async function saveDesign(fields, files = {}) {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) if (v != null && v !== '') fd.append(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
  for (const [k, f] of Object.entries(files)) if (f) fd.append(k, f, f.name || `${k}.png`);
  const s = getSession();
  const res = await fetch(`${API_BASE}/library/designs`, { method: 'POST', headers: { Authorization: `Bearer ${s && s.token}` }, body: fd });
  const j = await res.json().catch(() => ({}));
  return res.ok && j.success ? { ok: true, design: j.data } : { ok: false, error: j.error || `Save failed (${res.status})` };
}

/** Draws `blob` at most `max` px on the long side, halving step by step with high-quality smoothing (one big drawImage from
 * a 9000 px PDF page down to a small canvas skips pixels and looks jagged — owner 2026-10-26). */
async function resized(bmp, max, type, quality) {
  const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const tw = Math.max(1, Math.round(bmp.width * k));
  const th = Math.max(1, Math.round(bmp.height * k));
  let src = bmp; let sw = bmp.width; let sh = bmp.height;
  while (sw / 2 > tw && sh / 2 > th) { // halve until one step from the target
    const step = document.createElement('canvas');
    step.width = Math.round(sw / 2); step.height = Math.round(sh / 2);
    const sc = step.getContext('2d'); sc.imageSmoothingEnabled = true; sc.imageSmoothingQuality = 'high';
    sc.drawImage(src, 0, 0, step.width, step.height);
    src = step; sw = step.width; sh = step.height;
  }
  const c = document.createElement('canvas');
  c.width = tw; c.height = th;
  const ctx = c.getContext('2d'); ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, tw, th);
  return new Promise((r) => c.toBlob(r, type, quality));
}

/**
 * What a library save sends with the original: the stored thumbnail (thumbnail.png, 800 px — also read by the StudioCore
 * apps) and the 800 / 1600 px WebP copies Brightivity shows on cards, in detail views and in the Print preview (the
 * Library keeps them next to the original; big originals are over the size the server can resize).
 * @returns {{file: File, width: number, height: number, previews: {preview800: File, preview1600: File}}}
 */
export async function makeThumbnail(blob, max = 800) {
  const bmp = await createImageBitmap(blob);
  const [thumb, p800, p1600] = await Promise.all([resized(bmp, max, 'image/png'), resized(bmp, 800, 'image/webp', 0.9), resized(bmp, 1600, 'image/webp', 0.9)]);
  const webp = (b, w) => (b && b.type === 'image/webp' ? new File([b], `preview${w}.webp`, { type: 'image/webp' }) : null); // Safari < 17 has no WebP encoder: the server makes them
  return {
    file: new File([thumb], 'thumbnail.png', { type: 'image/png' }), width: bmp.width, height: bmp.height,
    previews: { preview800: webp(p800, 800), preview1600: webp(p1600, 1600) },
  };
}

export async function fetchDesignImage(design) {
  const url = design.imageUrl || design.thumbnailUrl;
  if (!url) throw new Error('This design has no picture to print.');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`The picture could not be loaded (${res.status}).`);
  const blob = await res.blob();
  const ext = blob.type === 'image/jpeg' ? 'jpg' : 'png';
  return new File([blob], `${(design.name || 'design').replace(/[\\/:*?"<>|]+/g, ' ').trim()}.${ext}`, { type: blob.type || 'image/png' });
}
